import React, { ReactElement } from 'react';
import { SegmentedControl, SegmentedControlOption } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../context/analytics-context';
import { setActiveCardIndex, useCardContext } from '../../context/card-context';
import { CardFaceData, CardFacePreviewImage } from '../../global-types';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { CardFaceToggleProps } from './card-face-toggle-types';
import styles from './card-face-toggle.module.scss';
import { getCardFaceLabel } from './utils';

const DEFAULT_TEST_ID = 'card-face-toggle';

const getFaceSegmentValue = (face: CardFaceData | CardFacePreviewImage): string =>
  face.faceNumber != null ? `face-${face.faceNumber}` : `type-${face.type}`;

export const CardFaceToggle = ({
  faces,
  slideIndex,
  handleIndexChange,
  mode,
  testId = DEFAULT_TEST_ID,
}: CardFaceToggleProps): ReactElement | null => {
  const { cardDispatch } = useCardContext();
  const { pageNavigationButtonClicked } = useAnalyticsContext();
  const isOneToMany = useIsOneToMany();

  const visibleFaces: Array<CardFaceData | CardFacePreviewImage> =
    mode === 'edit'
      ? (faces as CardFaceData[]).filter((face) => face.editorDisplayIndicator)
      : (faces as CardFacePreviewImage[]);

  if (!visibleFaces.length) {
    return null;
  }

  const options: SegmentedControlOption[] = visibleFaces.map((face) => ({
    value: getFaceSegmentValue(face),
    label: getCardFaceLabel(face, isOneToMany),
  }));

  const activeFace = visibleFaces.find((_, visibleFaceIndex) => {
    return visibleFaceIndex === slideIndex;
  });
  const activeValue = activeFace ? getFaceSegmentValue(activeFace) : '';

  const onChange = (value: string) => {
    if (value === activeValue) {
      return;
    }

    const visibleIndex = visibleFaces.findIndex((face) => getFaceSegmentValue(face) === value);
    if (visibleIndex < 0) {
      throw new Error(`Unable to find card face for segment value "${value}"`);
    }

    const face = visibleFaces.find((visibleFace) => {
      return getFaceSegmentValue(visibleFace) === value;
    });
    if (!face) {
      throw new Error(`Unable to resolve card face for segment value "${value}"`);
    }

    pageNavigationButtonClicked.current = 'Thumbnail';
    // Preserve legacy behavior: dispatch the absolute card-face index from
    // `faceNumber` while still notifying the parent of the visible-list
    // index so navigation hooks update slideCardIndex.
    if (face && face.faceNumber != null) {
      setActiveCardIndex(cardDispatch, face.faceNumber - 1);
    }
    handleIndexChange(visibleIndex);
  };

  return (
    <SegmentedControl
      className={styles['card-face-toggle']}
      options={options}
      value={activeValue}
      onChange={onChange}
      testId={testId}
    />
  );
};
